import { useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import theme, { FONT_GSS_FUTURA_SEMI_BOLD } from '@monorepo/shared/src/themes/defaultTheme';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { Group, Maybe, Student } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import useAssignments from 'state/hooks/use-assignments';
import { GroupTypes } from 'state/providers/AssignmentsProvider/types';
import { getIcon, getSortedGroups } from '../utils';
import { SCREEN_RESIZE_MEDIA_QUERY } from '@monorepo/shared/src/types';
import { IconSizes } from 'types';

const tAssignment = (key, options?) => translate('assignment', key, options);

type Props = {
  students: Maybe<Student[]> | undefined;
  groups: Maybe<Group[]> | undefined;
  assignedToAllGroups: boolean;
  useSelectedLabel?: boolean;
  allGroups?: Maybe<Group[]> | undefined;
};

type StyleProps = {
  useSelectedLabel?: boolean;
};

const AssignmentLetterLabels: React.FC<Props> = ({
  groups,
  students,
  assignedToAllGroups,
  useSelectedLabel,
  allGroups,
}) => {
  if (!groups?.length && !students?.length) return null;

  const classes = useStyles({ useSelectedLabel });
  const [{ groupFilter }] = useAssignments();

  const onlyOneGroup = allGroups?.length === 1;
  const sortedGroups = getSortedGroups(groups ?? []);

  const matchesLargerScreens = useMediaQuery(SCREEN_RESIZE_MEDIA_QUERY.LAPTOP);

  const renderLabel = (icon, name) => {
    return (
      <span key={name} className={classes.label}>
        {icon}
        <span className={classes.text}>
          {matchesLargerScreens &&
            (useSelectedLabel ? `${tAssignment('selected')}` : `${tAssignment('assignedTo')}`)}
          {name}
        </span>
      </span>
    );
  };
  const renderGroupLabel = (icon, name) => {
    if (name !== groupFilter && !onlyOneGroup && groupFilter !== GroupTypes.ALL) return null;
    return renderLabel(icon, name);
  };

  const renderStudentsLabel = () => {
    if (!students?.length) return;
    let displayLength = students.length;
    if (groupFilter !== GroupTypes.ALL && !onlyOneGroup) {
      displayLength = 0;
      students.forEach((student) => {
        student?.groups?.find((group) => group.name === groupFilter) && displayLength++;
      });
      if (!displayLength) return;
    }
    const icon = getIcon(GroupTypes.ALL, IconSizes.XS);
    const studentText = displayLength === 1 ? tAssignment('student') : tAssignment('students');
    const name = `${displayLength} ${studentText}`;
    return renderLabel(icon, name);
  };

  if (groupFilter === GroupTypes.ALL) {
    // if some groups and some students are assigned
    if (!assignedToAllGroups && groups && groups?.length > 1 && students?.length) {
      const icon = getIcon(GroupTypes.ALL, IconSizes.XS);
      const name = `${groups?.length} ${tAssignment('groups')}`;
      return (
        <>
          {renderGroupLabel(icon, name)}
          {renderStudentsLabel()}
        </>
      );
    }

    // if all groups are assigned
    if (assignedToAllGroups || (groups && groups?.length >= 3)) {
      const icon = getIcon(GroupTypes.ALL, IconSizes.XS);
      const name = assignedToAllGroups ? GroupTypes.ALL : `${groups?.length} Groups`;
      return (
        <>
          {renderGroupLabel(icon, name)}
          {renderStudentsLabel()}
        </>
      );
    }
  }

  return (
    <>
      {sortedGroups?.map((group) => {
        const icon = getIcon(group.name, IconSizes.XS);
        return renderGroupLabel(icon, group.name);
      })}
      {renderStudentsLabel()}
    </>
  );
};

const useStyles = makeStyles(() => ({
  label: {
    display: ({ useSelectedLabel }: StyleProps) => (useSelectedLabel ? 'inline-block' : 'flex'),
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${theme.palette.link.main}`,
    borderRadius: '80px',
    marginTop: '2px',
    width: 'fit-content',
  },
  text: {
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
    fontSize: remCalc(14),
    color: theme.palette.link.main,
    margin: '4px 5px 2px',
    lineHeight: remCalc(20),
  },
}));

export default AssignmentLetterLabels;
